import React, { useEffect, useState } from 'react';
import {
    Grid2,
    Box,
    Typography,
    Divider,
    styled,
    FormControl,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseButton from '../../Components/CloseButton';
import { IDDisplayBox } from '../../Theme/DigitalTwinStyles';
import FullScreenDialog from '../../Components/FullScreenDialog';
import DotSeparator from '../../Components/DotSeparator';
import CustomTag from '../../Components/CustomTag';
import { toTitleCase } from '../../Helper/Formatters/TextFormatter';
import { alertTagStyles } from '../../Helper/DigitalTwin/DataFile';
import { ActionGrid } from '../BmsFaults/Components/StyledComponents';
import IncidentActivityTimeline from '../BmsFaults/Components/IncidentActivityTimeline';
import CustomIcon from '../../Components/CustomIcon';
import { PrimaryFormLoadingActionButton } from '../../Features/Table/GenericDraggableTable/TableStyles';
import ActionChecklistItem from '../BmsFaults/Components/ActionChecklistItem';
import { convertAndGetTimeStampInRequiredFormat } from '../../Helper/DatePicker/DateConverter';
import moment from 'moment';
import { useToast } from '../../Hooks/useToast';
import { useBaseEntityInfo, usePagesContent } from '../../Hooks/usePagesContent';
import { CanUpdate } from '../../Components/PermissionGuards';
import { usePermissions } from '../../Context/PermissionContext';
import AlertConditionPopup from '../BmsFaults/Components/AlertConditionPopup';
import CartLoader from '../../Helper/Loaders/CartLoader';

const StyledSelect = styled(Select)(() => ({
    "& .MuiOutlinedInput-input .MuiTypography-root": {
        fontSize: '14px',
    },

    "& .MuiSvgIcon-root": {
        fontSize: '27px',
    },

    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: '#E6E6E6',
        borderWidth: "1px",
        borderRadius: '8px',
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6D72F6",
        borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6D72F6",
        borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-input .MuiTypography-root": {
        color: "#6D72F6",
    },
    "&.Mui-focused .MuiSvgIcon-root": {
        color: "#6D72F6",
    },
    "& .Mui-disabled": {
        cursor: "not-allowed",
    },
}));

const StyledTextField = styled(TextField)(() => ({
    "& .MuiOutlinedInput-root": {
        fontSize: "12px",
        lineHeight: "24px",
        padding: 0,

        "& fieldset": {
            borderColor: "#E6E6E6",
            borderWidth: "1px",
            borderRadius: '8px',
        },
        "&:hover fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "& textarea": {
            padding: '8px 16px',
            fontSize: "14px",
            lineHeight: "20px",
        },
        "& textarea::placeholder": {
            color: "#A4B2C1",
            opacity: 1,
        },
    },
    '& .MuiList-root': {
        padding: 0,
    }
}));

export const DEFAULT_ALERT_STATUS_OPTIONS = {
    // key: label
    'open': 'Open',
    'in_progress': 'In Progress',
    'action_completed': 'Action Completed',
    'force_resolved': 'Force Resolved',
};

/**
 * Domain-agnostic incident action / status-update dialog shared by BMS Faults and
 * Smart Alerts (Platform Alerts). All domain differences are injected via `config`:
 *
 *   config.featureCode        - RBAC feature code gating update / force_resolve
 *   config.api.logActivity        (incidentID, dataToSave) => Promise
 *   config.api.getActivityHistory (incidentID, params?)    => Promise
 *   config.api.getInfo            (incidentID)              => Promise (returns response.data)
 *   config.getInstructions    (infoData) => [{ instruction, instructionCode }]
 *   config.buildConditionRows (infoData) => rows for the View Condition popup
 *   config.resolvedStatuses   - status strings treated as "resolved" (hides the editor)
 *   config.statusOptions      - { statusKey: label } for the Update Status dropdown
 */
const IncidentActionDialog = ({
    actionData,
    open,
    onClose,
    config,
}) => {
    const {
        featureCode,
        api,
        getInstructions = (d) => d?.diagnosticsInstructions ?? [],
        buildConditionRows = () => [],
        resolvedStatuses = [],
        statusOptions: statusOptionsConfig = DEFAULT_ALERT_STATUS_OPTIONS,
        // Severity-level chip (LVx) is shown for BMS Faults but hidden for Smart
        // Alerts (Platform Alerts V2), which does not surface level anywhere.
        showLevel = true,
    } = config;

    const { baseEntityTypePluralLable, baseEntityTypeLable, baseEntityType, baseEntityTypeIDLable } = useBaseEntityInfo();
    const pageContent = usePagesContent();

    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const canUpdate = !isRbacV2Enabled() || hasPermission(featureCode, 'update');
    const canForceResolve = !isRbacV2Enabled() || hasPermission(featureCode, 'force_resolve');

    const { t } = useTranslation(['common', 'digital_twin']);
    const { openNotification } = useToast();

    const { battery, alertIncident } = actionData;
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIncidentStatus, setSelectedIncidentStatus] = useState();
    const [comment, setComment] = useState('');
    const [activityHistory, setActivityHistory] = useState([]);
    const [enableSaveAction, setEnableSaveAction] = useState(false);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [disableIncidentStatusDropdown, setDisableIncidentStatusDropdown] = useState(false);
    const [checklistData, setChecklistData] = useState({});
    const [originalChecklistData, setOriginalChecklistData] = useState({});
    const [instructionsMap, setInstructionsMap] = useState({});
    const [conditionInfoData, setConditionInfoData] = useState({});
    const [isConditionLoading, setIsConditionLoading] = useState(false);
    const [hasUpdatedData, setHasUpdatedData] = useState(false);
    const [alertStatusOptions, setAlertStatusOptions] = useState(statusOptionsConfig);
    const [currentBmsAlertStatus, setCurrentBmsAlertStatus] = useState(null);

    const getTagStyles = (key, value) => {
        return alertTagStyles[key][value?.toLowerCase()] || {}
    };

    const handleSubmit = async () => {
        if (alertIncident?.incidentID) {
            setEnableSaveAction(false);

            const dataToSave = {
                checklistCompleted: Object.entries(checklistData).filter(([, v]) => v).map(([k]) => k),
                checklistOpen: Object.entries(checklistData).filter(([, v]) => !v).map(([k]) => k),
            }

            // Include comment if valid
            if (comment) {
                dataToSave.comments = comment;
            }
            // Only include status if it has changed from the original
            if (selectedIncidentStatus !== 'open' && selectedIncidentStatus !== alertIncident?.incidentErrorStatus) {
                dataToSave.status = selectedIncidentStatus;
            }
            await api.logActivity(alertIncident?.incidentID, dataToSave);
            openNotification({ text: 'Changes updated successfully', icon: 'toast-success' })

            await fetchAlertIncidentHistory();

            if (!resolvedStatuses.includes(selectedIncidentStatus) && statusOptionsConfig[selectedIncidentStatus]) {
                setCurrentBmsAlertStatus(statusOptionsConfig[selectedIncidentStatus]);
            }

            // Mark that changes were saved during this session
            setHasUpdatedData(true);

            // reset comment
            setComment('');

            // Update the original checklist data to current state after save
            setOriginalChecklistData(checklistData);

            setEnableSaveAction(!resolvedStatuses.includes(selectedIncidentStatus));
            setDisableIncidentStatusDropdown(resolvedStatuses.includes(selectedIncidentStatus));

        }
    }

    const fetchAlertIncidentHistory = async () => {
        if (!alertIncident?.incidentID) {
            setIsTimelineLoading(false);
            return;
        }

        setIsTimelineLoading(true);
        const response = await api.getActivityHistory(alertIncident.incidentID);
        const activityResponse = response?.response?.data || [];

        // Sort oldest-first so each entry can be diffed against the one before it
        const sorted = [...activityResponse].sort(
            (a, b) => new Date(a.customerDBTime) - new Date(b.customerDBTime)
        );

        const formattedHistory = sorted.map((item, idx) => {
            const formattedDateTime = convertAndGetTimeStampInRequiredFormat(item.customerDBTime);
            const momentObj = moment(formattedDateTime, "YYYY-MM-DD HH:mm:ss");

            const date = momentObj.format("D MMMM YYYY");
            const time = momentObj.format("h:mm A");

            const prevCompleted = new Set(sorted[idx - 1]?.checklistCompleted || []);
            const currCompleted = new Set(item.checklistCompleted || []);

            return {
                date,
                time,
                sourceType: item.sourceType?.toLowerCase() || "system",
                userName:
                    item.updatedByName ||
                    (item.sourceType?.toLowerCase() === "system" ? "System" : "Unknown User"),
                comment: item.comments || "",
                status: item.finalStatus || item.bmsAlertStatus || item.lastUserUpdatedStatus || "",
                systemStatus: (item.errorStatus || item.alertStatus)?.toLowerCase() || "",
                checklistChecked:   [...currCompleted].filter(c => !prevCompleted.has(c)),
                checklistUnchecked: [...prevCompleted].filter(c => !currCompleted.has(c)),
            };
        });
        setActivityHistory(formattedHistory);
        setIsTimelineLoading(false);
    }

    const handleClose = () => {
        // Reset all state variables to prevent stale data on reopen
        setComment('');
        setActivityHistory([]);
        setSelectedIncidentStatus(null);
        setChecklistData({});
        setOriginalChecklistData({});
        setInstructionsMap({});
        setConditionInfoData({});
        setIsConditionLoading(false);
        setEnableSaveAction(false);
        setDisableIncidentStatusDropdown(false);
        setIsTimelineLoading(false);
        setAlertStatusOptions(statusOptionsConfig);
        setCurrentBmsAlertStatus(null);

        // Pass whether changes were saved to the parent
        onClose(hasUpdatedData);

        // Reset hasUpdatedData after calling onClose
        setHasUpdatedData(false);
    }

    const fetchChecklist = async () => {
        if (!alertIncident?.incidentID) {
            return;
        }

        setIsConditionLoading(true);
        const [infoResponse, checklistOnlyResponse] = await Promise.all([
            api.getInfo(alertIncident.incidentID),
            api.getActivityHistory(alertIncident.incidentID, { checklistOnly: true })
        ]);

        const infoData = infoResponse?.response?.data ?? {};
        const instructions = getInstructions(infoData);
        const completedCodes = (checklistOnlyResponse?.response?.data?.[0]?.checklistCompleted || []).map(String);

        const map = {};
        const checklistDataObj = {};
        for (const { instruction, instructionCode } of instructions) {
            const code = String(instructionCode);
            map[code] = instruction;
            checklistDataObj[code] = completedCodes.includes(code);
        }

        setInstructionsMap(map);
        setChecklistData(checklistDataObj);
        setOriginalChecklistData(checklistDataObj);
        setConditionInfoData(infoData);
        setIsConditionLoading(false);
    }

    useEffect(() => {
        if (actionData?.alertIncident?.bmsAlertStatus) {
            setCurrentBmsAlertStatus(actionData.alertIncident.bmsAlertStatus);
        }
    }, [actionData]); // updated effect trigger to properly re-render when dialog is closed

    useEffect(() => {
        if (alertIncident?.incidentID) {
            setHasUpdatedData(false);
            fetchAlertIncidentHistory();
            fetchChecklist();
            // If status is in a resolved state, set initial user status to null.
            // If unresolved, and incidentErrorStatus is null or empty, default it to "Open"
            const initialStatus = !resolvedStatuses.includes(alertIncident?.bmsAlertStatus) ? alertIncident?.incidentErrorStatus ?? 'open' : null;
            setSelectedIncidentStatus(initialStatus);
            if (resolvedStatuses.includes(initialStatus)) {
                setAlertStatusOptions({ [initialStatus]: initialStatus });
            }
            setEnableSaveAction(!resolvedStatuses.includes(initialStatus));
            setDisableIncidentStatusDropdown(resolvedStatuses.includes(initialStatus));
        }
    }, [actionData]);

    useEffect(() => {
        const checkFormEdit = () => {
            if (comment !== '') {
                return true;
            }

            if (selectedIncidentStatus !== 'open' && selectedIncidentStatus !== alertIncident?.incidentErrorStatus) {
                return true;
            }

            // Check for checklist edits by comparing current state with original
            const hasChecklistEdits = Object.keys(checklistData).some(
                instruction => checklistData[instruction] !== originalChecklistData[instruction]
            );

            if (hasChecklistEdits) {
                return true;
            }

            return false;
        };

        const isEdited = checkFormEdit();
        setEnableSaveAction(isEdited);
    }, [comment, selectedIncidentStatus, checklistData, originalChecklistData, alertIncident?.incidentErrorStatus])

    return (
        <FullScreenDialog open={open} handleClose={handleClose} loading={isLoading} containerStyles={{ bgcolor: '#FAFAFA' }}>
                <Grid2 container flexDirection={'column'} px={3} py={2} gap={1.25} sx={{ bgcolor: '#FFFFFF' }}>
                    <Grid2 container justifyContent={'space-between'} alignItems={'center'}>
                        <Grid2 container alignItems={'center'} gap={1} >
                            <Typography variant="fw500s16lh20c000000" sx={{ lineHeight: '20px' }}>{t(alertIncident?.incidentErrorName)}</Typography>
                            <AlertConditionPopup
                                title={t(alertIncident?.incidentErrorName)}
                                heading={t('Fault Conditions')}
                                triggerLabel={t('View Condition')}
                                columns={pageContent?.metaInfo?.analyse?.viewConditions?.data ?? []}
                                rows={buildConditionRows(conditionInfoData)}
                                isVisible={pageContent?.metaInfo?.analyse?.viewConditions?.isVisible === true}
                                isLoading={isConditionLoading}
                            />
                        </Grid2>

                        <Grid2 container justifyContent={'center'} alignItems={'center'} gap={1.75}>
                            <CloseButton onClick={handleClose} />
                        </Grid2>
                    </Grid2>
                    <Grid2 container alignItems={'center'} gap={1.25}>
                        {currentBmsAlertStatus && (
                            <CustomTag
                                icon="dot-icon"
                                text={toTitleCase(currentBmsAlertStatus)}
                                fontColor={getTagStyles('bmsAlertStatus', currentBmsAlertStatus).fontColor}
                                backgroundColor={getTagStyles('bmsAlertStatus', currentBmsAlertStatus).backgroundColor}
                                fontSize={'12px'}
                                iconProps={{
                                    sx: { display: 'none' }
                                }}
                                containerStyle={{
                                    borderRadius: '12px',
                                }}
                            />
                        )}
                        <CustomTag
                            text={alertIncident?.severity}
                            backgroundColor={getTagStyles('severity', alertIncident?.severity).backgroundColor}
                            fontColor={getTagStyles('severity', alertIncident?.severity).fontColor}
                            fontSize={'12px'}
                        />
                        {showLevel && (
                            <CustomTag
                                text={`LV${alertIncident?.severityLevel}`}
                                backgroundColor={getTagStyles('severityLevel', 'default').backgroundColor}
                                fontColor={getTagStyles('severityLevel', 'default').fontColor}
                                fontSize={'12px'}
                            />
                        )}
                        <IDDisplayBox sx={{ py: 0 }}>
                            <Typography variant='fw400s12c494949' sx={{ lineHeight: '24px !important' }}>
                                {baseEntityTypeIDLable}:&nbsp;
                                {baseEntityType.toLowerCase() === "vehicle" ? battery?.licensePlateNo : battery?.deviceID}
                            </Typography>
                            <DotSeparator color="#000000" />
                            <Typography variant='fw500s12c000' sx={{ lineHeight: '16px' }}>{t('Entity affected')}: {toTitleCase(alertIncident?.entityAffectedDisplayID)}   </Typography>
                        </IDDisplayBox>
                    </Grid2>
                </Grid2>
                <Divider />
                <Grid2 container gap={2} px={3} py={2}>
                    <Grid2 container flexDirection={'column'} flexWrap={'nowrap'} gap={2} width={'60%'} height={'calc(100vh - 130px)'} sx={{ overflow: 'auto' }}>
                        {!resolvedStatuses.includes(currentBmsAlertStatus) && (<FormControl sx={{ width: '100%' }}>
                            <ActionGrid container flexDirection={'column'}>
                                <Grid2 container gap={1} alignItems={'center'}>
                                    <Typography variant='fw600s16c000000' sx={{ lineHeight: '20px' }}>Update Status</Typography>
                                </Grid2>
                                <StyledSelect
                                    value={selectedIncidentStatus}
                                    onChange={(e) => {
                                        setSelectedIncidentStatus(e.target.value)
                                    }}
                                    disabled={disableIncidentStatusDropdown || !canUpdate}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    renderValue={(selected) => {
                                        return (
                                            <Typography variant="fw400s16c000000" sx={{ color: '#3D4B5A', lineHeight: '20px' }}>
                                                {alertStatusOptions[selected]}
                                            </Typography>
                                        );
                                    }}
                                >
                                    {Object.entries(alertStatusOptions).map(([key, value]) => {
                                        const isOpenStatus = key === 'open';

                                        if (key === 'force_resolved') {
                                            // MenuItem must be a DIRECT child of Select for MUI to wire
                                            // its click/value handling — so gate with a boolean rather
                                            // than wrapping in <CanForceResolve>, which would render the
                                            // item but leave it unselectable.
                                            if (!canForceResolve) return null;
                                            return (
                                                <MenuItem
                                                    key={key}
                                                    value={key}
                                                    sx={{ py: 1, pl: 1.5 }}
                                                >
                                                    <Typography variant="fw400s16c000000" sx={{ color: '#3D4B5A', lineHeight: '20px' }}>{value}</Typography>
                                                </MenuItem>
                                            );
                                        }

                                        return (
                                            <MenuItem
                                                key={key}
                                                value={key}
                                                sx={{ py: 1, pl: 1.5 }}
                                                disabled={isOpenStatus}
                                            >
                                                <Typography variant="fw400s16c000000" sx={{ color: isOpenStatus ? '#A4B2C1' : '#3D4B5A', lineHeight: '20px' }}>{value}</Typography>
                                            </MenuItem>
                                        );
                                    })}
                                </StyledSelect>
                            </ActionGrid>
                        </FormControl>)}
                        <CanUpdate feature={featureCode}>
                            <ActionGrid container>
                                <Grid2 container alignItems={'flex-end'} gap={1}>
                                    <CustomIcon name="comment-icon" fill="#A4B2C1" />
                                    <Typography variant='fw600s16c000000' sx={{ lineHeight: '20px' }}>Add comments</Typography>
                                </Grid2>
                                <StyledTextField
                                    fullWidth
                                    autoComplete="off"
                                    variant="outlined"
                                    placeholder="Type here"
                                    value={comment}
                                    onChange={(e) => {
                                        setComment(e.target.value)
                                    }}
                                    multiline
                                    minRows={5}
                                    sx={{ flex: 1, minWidth: "150px" }}
                                />
                            </ActionGrid>
                        </CanUpdate>
                        <ActionGrid container>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant='fw600s16c000000' sx={{ lineHeight: '20px' }}>Action Checklist</Typography>
                                <Typography variant='fw400s12c8F8F8F' sx={{ color: '#87939D', lineHeight: '18px', fontStyle: 'italic' }}>
                                    <strong>Note:</strong> These are guidelines, not mandatory steps. If you handled it differently, leave a note in the comments.
                                </Typography>
                            </Box>
                            {isConditionLoading ? (
                                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <CartLoader />
                                </Box>
                            ) : Object.keys(checklistData).length === 0 ? (
                                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant='fw400s14c8F8F8F' sx={{ color: '#87939D' }}>
                                        No action checklist data available
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid2 container flexDirection={'column'} gap={1}>
                                    {Object.entries(checklistData).map(([instructionCode, isChecked]) => (
                                        <ActionChecklistItem
                                            key={instructionCode}
                                            label={instructionsMap[instructionCode] ?? instructionCode}
                                            isChecked={isChecked}
                                            onChange={() => {
                                                setChecklistData(prev => ({
                                                    ...prev,
                                                    [instructionCode]: !prev[instructionCode]
                                                }));
                                            }}
                                        />
                                    ))}
                                </Grid2>
                            )}
                        </ActionGrid>
                    </Grid2>
                    <Grid2 container flexDirection={'column'} gap={2} flex={1}>
                        <IncidentActivityTimeline isLoading={isTimelineLoading} activityHistory={activityHistory} instructionsMap={instructionsMap} />
                        <CanUpdate feature={featureCode}>
                            <Grid2 container justifyContent={'flex-end'}>
                                <PrimaryFormLoadingActionButton
                                    disabled={!enableSaveAction}
                                    onClick={handleSubmit}
                                    loading={false}
                                    sx={{ borderRadius: '4px', px: 1.5 }}
                                >
                                    Save Changes
                                </PrimaryFormLoadingActionButton>
                            </Grid2>
                        </CanUpdate>
                    </Grid2>
                </Grid2>
        </FullScreenDialog>
    );
};

export default IncidentActionDialog;
